import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from './adminApi';
import { Plus, Edit2, Trash2, Search, ExternalLink, FileText, Copy } from 'lucide-react';

const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [cloningSlug, setCloningSlug] = useState('');
  const navigate = useNavigate();

  const refresh = () => {
    setLoading(true);
    adminApi.listArticles().then(setArticles).finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.slug.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
    );
  }, [articles, query]);

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete the article "${slug}" permanently?`)) return;
    await adminApi.deleteArticle(slug);
    setArticles((prev) => prev.filter((a) => a.slug !== slug));
  };

  const handleClone = async (a) => {
    setCloningSlug(a.slug);
    try {
      const stamp = Date.now().toString(36).slice(-4);
      const baseSlug = (a.slug || 'article').replace(/-copy(-[a-z0-9]+)?$/, '');
      const newSlug = `${baseSlug}-copy-${stamp}`;
      const created = await adminApi.createArticle({
        slug: newSlug,
        title: `${a.title} (copy)`,
        excerpt: a.excerpt,
        body: a.body,
        category: a.category,
        image: a.image,
        readTime: a.readTime,
        author: a.author,
        metaDescription: a.metaDescription || '',
        published: false, // clone is a draft by default
        publishedAt: null,
        tags: Array.isArray(a.tags) ? a.tags : [],
      });
      navigate(`/admin/articles/${created.slug}`);
    } catch (err) {
      window.alert(err?.response?.data?.detail || 'Clone failed.');
    } finally {
      setCloningSlug('');
    }
  };

  return (
    <div className="px-8 lg:px-12 py-10" data-testid="admin-articles-page">
      <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">Content</p>
          <h1 className="text-white text-4xl font-light font-['Inter']">Blog Editor</h1>
          <p className="text-white/40 text-sm font-['Lato'] mt-2">
            Create, edit and publish marketing insights.
          </p>
        </div>
        <Link
          to="/admin/articles/new"
          data-testid="articles-new-button"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition"
        >
          <Plus className="w-4 h-4" /> New article
        </Link>
      </header>

      <div className="relative w-full md:w-80 mb-6">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search articles…"
          data-testid="articles-search"
          className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-white placeholder-white/30 font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#15151A] border border-white/5 rounded-2xl py-16 text-center" data-testid="articles-empty">
          <FileText className="w-8 h-8 text-white/20 mx-auto mb-3" />
          <p className="text-white/50 text-sm font-['Lato']">No articles found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((a) => (
            <article
              key={a.slug}
              data-testid={`article-card-${a.slug}`}
              className="bg-[#15151A] border border-white/5 rounded-2xl overflow-hidden flex flex-col hover:border-[#83BF42]/30 transition-colors"
            >
              <div className="relative h-32 overflow-hidden">
                <img src={a.image} alt={a.title} className={`w-full h-full object-cover ${a.published === false ? 'opacity-40' : ''}`} />
                <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                  <span className="bg-black/50 backdrop-blur-sm text-white text-[10px] font-medium font-['Inter'] px-2.5 py-1 rounded-full uppercase tracking-wider">
                    {a.category}
                  </span>
                  {a.published === false && (
                    <span data-testid={`article-draft-badge-${a.slug}`} className="bg-amber-500/90 text-[#0F0F11] text-[10px] font-bold font-['Inter'] px-2.5 py-1 rounded-full uppercase tracking-wider">
                      Draft
                    </span>
                  )}
                  {a.published !== false && a.publishedAt && new Date(a.publishedAt) > new Date() && (
                    <span data-testid={`article-scheduled-badge-${a.slug}`} className="bg-blue-500/90 text-white text-[10px] font-bold font-['Inter'] px-2.5 py-1 rounded-full uppercase tracking-wider">
                      Scheduled
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-white text-base font-medium font-['Inter'] leading-snug line-clamp-2 mb-2">
                  {a.title}
                </h3>
                <p className="text-white/40 text-xs font-['Lato'] mb-4 line-clamp-2 flex-1">
                  {a.excerpt}
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/admin/articles/${a.slug}`}
                      data-testid={`article-edit-${a.slug}`}
                      className="inline-flex items-center gap-1.5 text-[#83BF42] text-xs font-['Inter'] hover:underline"
                    >
                      <Edit2 className="w-3 h-3" /> Edit
                    </Link>
                    <a
                      href={`/insights/${a.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-white/40 text-xs font-['Inter'] hover:text-white"
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </a>
                    <button
                      onClick={() => handleClone(a)}
                      disabled={cloningSlug === a.slug}
                      data-testid={`article-clone-${a.slug}`}
                      className="inline-flex items-center gap-1.5 text-white/40 text-xs font-['Inter'] hover:text-white disabled:opacity-50"
                    >
                      <Copy className="w-3 h-3" /> {cloningSlug === a.slug ? 'Cloning…' : 'Clone'}
                    </button>
                  </div>
                  <button
                    onClick={() => handleDelete(a.slug)}
                    data-testid={`article-delete-${a.slug}`}
                    className="text-red-400/70 hover:text-red-400 p-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminArticles;
